/**
 * Lightweight utility functions for Turkish phone numbers
 * Replaces the heavier react-phone-number-input and libphonenumber-js libraries
 */

/**
 * Normalize Turkish phone numbers to simple 10-digit format (5XXXXXXXXX)
 * 
 * @param phoneInput The phone number to normalize
 * @returns Normalized phone number in 10-digit format
 */
export const normalizeTurkishPhoneNumber = (phoneInput: string): string => {
  if (!phoneInput) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneInput.replace(/\D/g, '');
  
  // Handle different input formats
  if (digitsOnly.length >= 10) {
    // If starts with 0, it's likely 05XX format - remove the leading 0
    if (digitsOnly.startsWith('0') && digitsOnly.length >= 11) {
      return digitsOnly.substring(1, 11);
    }
    // If has country code (90...)
    else if (digitsOnly.startsWith('90') && digitsOnly.length >= 12) {
      return digitsOnly.substring(2, 12);
    }
    // Just the 10-digit number (5XXXXXXXXX)
    else if (digitsOnly.length >= 10 && digitsOnly.startsWith('5')) {
      return digitsOnly.substring(0, 10);
    }
  }
  
  // Return what we have (might be partial number)
  return digitsOnly;
};

/**
 * Checks if a Turkish phone number is valid
 * 
 * @param phoneNumber The phone number to validate
 * @returns True if valid, false otherwise
 */
export const isPhoneNumberValid = (phoneNumber: string): boolean => {
  if (!phoneNumber) return false;
  
  // Normalize first to handle different input formats
  const normalized = normalizeTurkishPhoneNumber(phoneNumber);
  
  // Valid Turkish mobile numbers should be 10 digits starting with 5
  return /^5\d{9}$/.test(normalized);
};

/**
 * Format a Turkish phone number for display
 * 
 * @param phoneNumber The phone number to format
 * @returns Formatted phone number for display
 */
export const formatTurkishPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Strip country code and leading zeros if present
  let nationalNumber = digitsOnly;
  if (digitsOnly.startsWith('90') && digitsOnly.length > 10) {
    nationalNumber = digitsOnly.substring(2);
  } else if (digitsOnly.startsWith('0')) {
    nationalNumber = digitsOnly.substring(1);
  }
  
  // Get only the first 10 digits if longer
  if (nationalNumber.length > 10) {
    nationalNumber = nationalNumber.substring(0, 10);
  }
  
  // Format based on length
  if (nationalNumber.length <= 3) {
    return nationalNumber;
  } else if (nationalNumber.length <= 6) {
    return `${nationalNumber.substring(0, 3)} ${nationalNumber.substring(3)}`;
  } else if (nationalNumber.length <= 8) {
    return `${nationalNumber.substring(0, 3)} ${nationalNumber.substring(3, 6)} ${nationalNumber.substring(6)}`;
  } else {
    return `${nationalNumber.substring(0, 3)} ${nationalNumber.substring(3, 6)} ${nationalNumber.substring(6, 8)} ${nationalNumber.substring(8)}`;
  }
};

/**
 * Backward compatibility: these functions maintain the previous API
 * but use our new lightweight implementations
 */
export const normalizePhoneNumber = normalizeTurkishPhoneNumber;
export const formatPhoneForDisplay = formatTurkishPhoneNumber; 